import React, { useState, useEffect, createContext, useContext } from 'react';
import { Loader2 } from 'lucide-react';
import { showErrorToast, showSuccessToast, showInfoToast } from '@/utils/error-handler';

// Auth context for managing authentication state
interface AuthContextType {
  user: { id: string; username: string; isAdmin?: boolean } | null;
  token: string | null;
  isLoading: boolean;
  login: (user: { id: string; username: string; isAdmin?: boolean }, token: string) => void;
  logout: () => void;
  checkAuth: () => Promise<void>;
  lastSyncEvent: string | null; // For debugging cross-tab sync
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<{ id: string; username: string; isAdmin?: boolean } | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastSyncEvent, setLastSyncEvent] = useState<string | null>(null);

  const login = (userData: { id: string; username: string; isAdmin?: boolean }, authToken: string) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem('auth-token', authToken);
  };

  const logout = async () => {
    // Call logout endpoint to blacklist token
    if (token) {
      try {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
      } catch (error) {
        console.error('Logout API call failed:', error);
      }
    }

    // Clear local state first
    setUser(null);
    setToken(null);
    
    // Remove token from localStorage (this will trigger storage event in other tabs)
    localStorage.removeItem('auth-token');
    
    showSuccessToast('Logged out successfully', 'You have been signed out of all tabs');
    console.log('Logged out successfully');
  };

  const checkAuth = async () => {
    setIsLoading(true);
    
    try {
      const storedToken = localStorage.getItem('auth-token');
      if (!storedToken) {
        setIsLoading(false);
        return;
      }

      // Verify token with server
      const response = await fetch('/api/auth/me', {
        headers: {
          'Authorization': `Bearer ${storedToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setToken(storedToken);
      } else {
        // Token is invalid, clear it
        localStorage.removeItem('auth-token');
        setUser(null);
        setToken(null);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      // Clear invalid token
      localStorage.removeItem('auth-token');
      setUser(null);
      setToken(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();

    // Listen for storage changes to sync authentication across tabs
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'auth-token') {
        if (e.newValue === null) {
          // Token was removed in another tab, log out this tab
          console.log('Token removed in another tab, logging out...');
          setLastSyncEvent('Logged out from another tab');
          showInfoToast('Logged out', 'You were logged out from another tab');
          setUser(null);
          setToken(null);
        } else if (e.newValue !== token) {
          // Token was updated in another tab, refresh auth state
          console.log('Token updated in another tab, refreshing auth...');
          setLastSyncEvent('Token updated from another tab');
          showInfoToast('Session updated', 'Your session was updated from another tab');
          checkAuth();
        }
      }
    };

    // Add event listener for storage changes
    window.addEventListener('storage', handleStorageChange);

    // Set up periodic token validation (every 5 minutes)
    const tokenValidationInterval = setInterval(() => {
      if (token) {
        console.log('Performing periodic token validation...');
        checkAuth();
      }
    }, 5 * 60 * 1000); // 5 minutes

    // Check auth when user returns to the tab
    const handleVisibilityChange = () => {
      if (!document.hidden && token) {
        console.log('Tab became visible, checking auth...');
        checkAuth();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup event listeners and interval on unmount
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(tokenValidationInterval);
    };
  }, [token]);

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    login,
    logout,
    checkAuth,
    lastSyncEvent,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

interface AuthGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  requireAuth?: boolean;
}

export function AuthGuard({ 
  children, 
  fallback = <AuthLoadingScreen />, 
  requireAuth = true 
}: AuthGuardProps) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return <>{fallback}</>;
  }

  if (requireAuth && !user) {
    return <AuthRequired />;
  }

  if (!requireAuth && user) {
    return <AlreadyAuthenticated />;
  }

  return <>{children}</>;
}

function AuthLoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center space-y-4">
        <Loader2 className="h-8 w-8 animate-spin mx-auto" />
        <p className="text-muted-foreground">Checking authentication...</p>
      </div>
    </div>
  );
}

function AuthRequired() {
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="text-center space-y-4 max-w-md">
        <h2 className="text-2xl font-bold">Authentication Required</h2>
        <p className="text-muted-foreground">
          You need to be signed in to access this page.
        </p>
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Please sign in or create an account to continue.
          </p>
        </div>
      </div>
    </div>
  );
}

function AlreadyAuthenticated() {
  const { user } = useAuth();
  
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="text-center space-y-4 max-w-md">
        <h2 className="text-2xl font-bold">Welcome back, {user?.username}!</h2>
        <p className="text-muted-foreground">
          You are already signed in.
        </p>
      </div>
    </div>
  );
}

// Higher-order component for protecting routes
export function withAuth<P extends object>(
  Component: React.ComponentType<P>,
  requireAuth: boolean = true
) {
  return function AuthenticatedComponent(props: P) {
    return (
      <AuthGuard requireAuth={requireAuth}>
        <Component {...props} />
      </AuthGuard>
    );
  };
}